import React, { useEffect, useState } from 'react';
import api, { asArray } from '../../services/api';

/** Reset a user's password to one the admin types. Stored passwords are one-way
 *  hashes, so the current one can't be shown — only replaced. */
function ResetPasswordDialog({ user, onClose }) {
  const [custom, setCustom] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const valid = custom.trim().length >= 6;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    setError('');
    try {
      await api.post(`/admin/users/${user.id}/reset-password`, { password: custom.trim() });
      setResult(custom.trim());
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-base font-bold text-slate-900">Reset password</h2>
        <p className="mt-0.5 text-xs text-slate-500">{user.fullName || '—'} · {user.email || user.phoneNumber}</p>

        {result ? (
          <>
            <p className="mt-4 text-xs font-semibold text-slate-600">New password</p>
            <div className="mt-1 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
              <code className="flex-1 select-all break-all font-mono text-base font-bold text-emerald-800">{result}</code>
              <button type="button" onClick={copy} className="shrink-0 rounded-lg bg-white px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100">
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">
              Saved. Share it with the user privately — they can change it from Reset password after signing in.
            </p>
            <button type="button" onClick={onClose} className="mt-4 w-full rounded-xl bg-slate-900 py-2 text-sm font-semibold text-white hover:bg-slate-800">
              Done
            </button>
          </>
        ) : (
          <>
            <label className="mt-4 block text-xs font-semibold text-slate-600" htmlFor="reset-pw">
              New password
            </label>
            <input
              id="reset-pw"
              type="text"
              autoComplete="off"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Min 6 characters"
              className="fs-input mt-1 font-mono"
            />
            {error && <p className="mt-2 text-xs text-rose-600">{error}</p>}
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={onClose} className="flex-1 rounded-xl bg-slate-100 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200">
                Cancel
              </button>
              <button type="button" onClick={submit} disabled={busy || !valid} className="flex-1 rounded-xl bg-slate-900 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                {busy ? 'Saving…' : 'Save password'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [payments, setPayments] = useState([]);
  const [detailUser, setDetailUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);

  const load = () => {
    const url = filter === 'active' ? '/admin/users/active-subscribers' :
                filter === 'inactive' ? '/admin/users/inactive' : '/admin/users';
    api.get(url).then(r => setUsers(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, [filter]);

  const toggleActive = async (id, current) => {
    await api.patch(`/admin/users/${id}/active?active=${!current}`);
    load();
  };

  const toggleSub = async (id, current) => {
    await api.patch(`/admin/users/${id}/subscription?active=${!current}`);
    load();
  };

  const deleteUser = async (u) => {
    if (u.role === 'ROLE_ADMIN') {
      alert('Cannot delete an admin account');
      return;
    }
    const ok = window.confirm(
      `Permanently delete ${u.fullName || u.email || 'this user'} and all related records (payments, messages, ratings)? This cannot be undone.`
    );
    if (!ok) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      if (selectedUserId === u.id) {
        setSelectedUserId(null);
        setPayments([]);
        setDetailUser(null);
      }
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  const showDetails = async (u) => {
    if (selectedUserId === u.id) {
      setSelectedUserId(null);
      setPayments([]);
      setDetailUser(null);
      return;
    }
    setSelectedUserId(u.id);
    setDetailUser(u);
    try {
      const res = await api.get(`/payment/user/${u.id}`);
      setPayments(asArray(res.data));
    } catch {
      setPayments([]);
    }
  };

  const lastPayment = (userId) => {
    // used only when expanded
    return null;
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Manage Users</h1>
      <div className="flex flex-wrap gap-1 bg-slate-100 rounded-xl p-1 mb-4 w-fit">
        {['all', 'active', 'inactive'].map(f => (
          <button key={f} type="button" onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-lg transition ${filter === f ? 'bg-slate-900 text-white font-semibold' : 'text-slate-600 hover:text-slate-900'}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left p-3">User</th>
              <th className="text-left p-3">Contact</th>
              <th className="text-left p-3">Account</th>
              <th className="text-left p-3">Subscription</th>
              <th className="text-left p-3">Trial / Dates</th>
              <th className="text-left p-3">Last login · IP · Location</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <React.Fragment key={u.id}>
                <tr className="border-t border-slate-50 align-top">
                  <td className="p-3">
                    <div className="font-medium">{u.fullName || '—'}</div>
                    <div className="text-[11px] text-slate-400">ID {u.id} · {u.role?.replace('ROLE_', '')}</div>
                  </td>
                  <td className="p-3">
                    <div>{u.email}</div>
                    <div className="text-xs text-slate-400">{u.phoneNumber}</div>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${u.active ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                      {u.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      u.subscriptionActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {u.subscriptionActive ? 'Subscribed' : 'Not subscribed'}
                    </span>
                    {u.paymentInfo && (
                      <div className="text-[11px] text-slate-400 mt-1">Card {u.paymentInfo}</div>
                    )}
                  </td>
                  <td className="p-3 text-xs text-slate-500 space-y-0.5">
                    <div>Trial used: {u.freeTrialUsed ? 'Yes' : 'No'}</div>
                    {u.subscriptionStartDate && (
                      <div>Start: {new Date(u.subscriptionStartDate).toLocaleDateString()}</div>
                    )}
                    {u.subscriptionEndDate && (
                      <div>End: {new Date(u.subscriptionEndDate).toLocaleDateString()}</div>
                    )}
                    {u.createdAt && (
                      <div className="text-slate-400">Joined: {new Date(u.createdAt).toLocaleDateString()}</div>
                    )}
                  </td>
                  <td className="p-3 text-xs text-slate-600 space-y-0.5">
                    <div className="font-medium text-slate-800">
                      {u.lastLogin ? new Date(u.lastLogin).toLocaleString() : '—'}
                    </div>
                    <div className="font-mono text-[11px] text-slate-500">{u.lastLoginIp || 'IP —'}</div>
                    <div className="text-[11px] text-slate-500">{u.lastLoginLocation || 'Location —'}</div>
                  </td>
                  <td className="p-3 space-y-1">
                    <button onClick={() => showDetails(u)}
                      className="block text-xs bg-sky-50 text-sky-700 hover:bg-sky-100 px-2 py-1 rounded w-full text-left">
                      {selectedUserId === u.id ? 'Hide payments' : 'Payments & status'}
                    </button>
                    <button onClick={() => toggleActive(u.id, u.active)}
                      className="block text-xs bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded w-full text-left">
                      {u.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button onClick={() => toggleSub(u.id, u.subscriptionActive)}
                      className="block text-xs bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded w-full text-left">
                      {u.subscriptionActive ? 'End sub' : 'Enable sub'}
                    </button>
                    {u.role !== 'ROLE_ADMIN' && (
                      <button onClick={() => setResetUser(u)}
                        className="block text-xs bg-amber-50 text-amber-800 hover:bg-amber-100 px-2 py-1 rounded w-full text-left">
                        Reset password
                      </button>
                    )}
                    {u.role !== 'ROLE_ADMIN' && (
                      <button onClick={() => deleteUser(u)}
                        className="block text-xs bg-rose-50 text-rose-700 hover:bg-rose-100 px-2 py-1 rounded w-full text-left">
                        Delete user
                      </button>
                    )}
                  </td>
                </tr>
                {selectedUserId === u.id && (
                  <tr className="bg-slate-50">
                    <td colSpan={7} className="p-4">
                      <div className="grid sm:grid-cols-2 gap-4 mb-3 text-xs">
                        <div className="bg-white rounded-lg border border-slate-100 p-3">
                          <p className="font-semibold text-slate-600 mb-1">Subscription detail</p>
                          <p>Status: <strong>{u.subscriptionActive ? 'ACTIVE' : 'INACTIVE'}</strong></p>
                          <p>Start: {u.subscriptionStartDate ? new Date(u.subscriptionStartDate).toLocaleString() : '—'}</p>
                          <p>End: {u.subscriptionEndDate ? new Date(u.subscriptionEndDate).toLocaleString() : '—'}</p>
                          <p>Free trial used: {u.freeTrialUsed ? 'Yes' : 'No'}</p>
                        </div>
                        <div className="bg-white rounded-lg border border-slate-100 p-3">
                          <p className="font-semibold text-slate-600 mb-1">Payment summary</p>
                          <p>Records: {payments.length}</p>
                          <p>Last paid:{' '}
                            {payments.find(p => p.status === 'PAID')
                              ? new Date(payments.find(p => p.status === 'PAID').paidAt || payments.find(p => p.status === 'PAID').createdAt).toLocaleString()
                              : '—'}
                          </p>
                          <p>Last amount:{' '}
                            {payments.find(p => p.status === 'PAID')
                              ? `$${payments.find(p => p.status === 'PAID').amount}`
                              : '—'}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs font-semibold text-slate-500 mb-2">Payment history</p>
                      {payments.length === 0 ? (
                        <p className="text-xs text-slate-400">No payments yet (trial only or unpaid)</p>
                      ) : (
                        <div className="space-y-1">
                          {payments.map(p => (
                            <div key={p.id} className="flex flex-wrap justify-between gap-2 text-xs bg-white rounded px-3 py-2 border border-slate-100">
                              <span>
                                <strong>${p.amount}</strong>
                                <span className="text-slate-400 mx-1">·</span>
                                {p.provider}
                                <span className={`ml-2 px-1.5 py-0.5 rounded ${
                                  p.status === 'PAID' ? 'bg-emerald-50 text-emerald-700' :
                                  p.status === 'PENDING' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'
                                }`}>{p.status}</span>
                                {p.description && <span className="text-slate-400 ml-1">({p.description})</span>}
                              </span>
                              <span className="text-slate-400">
                                {p.paidAt
                                  ? `Paid ${new Date(p.paidAt).toLocaleString()}`
                                  : `Created ${new Date(p.createdAt).toLocaleString()}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {resetUser && <ResetPasswordDialog user={resetUser} onClose={() => setResetUser(null)} />}
    </div>
  );
}
